import { useState } from 'react';
import { Button, Upload, Modal, Alert, Descriptions, Tag, Space, Tooltip, Typography, Flex, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { downloadGatheringConfig, readGatheringConfig } from '../../utils/gathering_config';
import { StepKindIcon, stepLabels } from './FlowTimeline';

const { Text } = Typography;

/**
 * Import of a gathering setup, with a preview of what's in it and what it
 * will replace before anything changes. Render-prop, so any trigger can open
 * it — the admin bar's small button, or the start chooser's card:
 *
 *   <ConfigImport ...>{({ uploadProps, previewConfig }) => (
 *       <Upload {...uploadProps}><Button>Import</Button></Upload>
 *   )}</ConfigImport>
 *
 * `previewConfig(config, source)` previews a config that didn't come from a
 * file (e.g. the sample setup).
 *
 *   currentGroups: this gathering's groups, to tell which picks survive
 *   onImport(config): resolves once the node has taken the new setup
 */
export function ConfigImport({ currentGroups, paletteColor, onImport, children }) {
    const [preview, setPreview] = useState(null); // { config, fileName }
    const [importing, setImporting] = useState(false);

    const readFile = file => {
        file.text().then(text => {
            const { config, error } = readGatheringConfig(text);
            if (error) message.error(error);
            else setPreview({ config, fileName: file.name });
        });
        return false; // read it here; never upload anywhere
    };

    const confirm = () => {
        setImporting(true);
        onImport(preview.config)
            .then(() => { message.success('Gathering setup imported'); setPreview(null); })
            .catch(() => {})
            .finally(() => setImporting(false));
    };

    const cfg = preview?.config;
    const current = new Set(currentGroups.map(g => g.name.trim().toLowerCase()));
    const kept = cfg ? cfg.groups.filter(g => current.has(g.name.trim().toLowerCase())) : [];
    const dropped = cfg ? currentGroups.filter(g => !cfg.groups.some(n => n.name.trim().toLowerCase() === g.name.trim().toLowerCase())) : [];
    const labels = cfg ? stepLabels(cfg.flow) : [];
    const totalMinutes = cfg ? cfg.flow.reduce((n, s) => n + s.minutes, 0) : 0;

    const uploadProps = { accept: '.json,application/json', showUploadList: false, beforeUpload: readFile };
    const previewConfig = (config, source) => setPreview({ config, fileName: source });

    return (
        <>
            {children({ uploadProps, previewConfig })}

            <Modal
                open={!!cfg}
                title="Import gathering setup"
                okText="Replace setup"
                okButtonProps={{ danger: true, loading: importing }}
                onOk={confirm}
                onCancel={() => setPreview(null)}
                width={600}
            >
                {cfg && (
                    <Flex vertical gap={16}>
                        <Descriptions
                            size="small"
                            column={1}
                            bordered
                            items={[
                                { key: 'file', label: 'Source', children: preview.fileName },
                                {
                                    key: 'from',
                                    label: 'From',
                                    children: `${cfg.source_node || 'unknown node'}${cfg.exported_at ? ` · ${new Date(cfg.exported_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : ''}`,
                                },
                                {
                                    key: 'room',
                                    label: 'Ideal room',
                                    children: `${cfg.room?.size} people · seats ${cfg.room?.pool === 'everyone' ? 'everyone' : 'people who picked a group'}`,
                                },
                                {
                                    key: 'groups',
                                    label: `Groups (${cfg.groups.length})`,
                                    children: cfg.groups.length ? (
                                        <Flex wrap gap={4}>
                                            {cfg.groups.map(g => (
                                                <Tag key={g.name} color={paletteColor(g.color)} style={{ margin: 0 }}>
                                                    {g.name}{g.seats_per_room ? ` · ${g.seats_per_room} seat${g.seats_per_room === 1 ? '' : 's'}` : ''}
                                                </Tag>
                                            ))}
                                        </Flex>
                                    ) : <Text type="secondary">None</Text>,
                                },
                                {
                                    key: 'topics',
                                    label: `Topics (${cfg.topics.length})`,
                                    children: cfg.topics.length ? (
                                        <Flex vertical gap={2}>
                                            {cfg.topics.slice(0, 5).map((t, i) => <Text key={i} ellipsis style={{ maxWidth: 380 }}>{i + 1}. {t}</Text>)}
                                            {cfg.topics.length > 5 && <Text type="secondary">and {cfg.topics.length - 5} more</Text>}
                                        </Flex>
                                    ) : <Text type="secondary">None</Text>,
                                },
                                {
                                    key: 'flow',
                                    label: `Flow (${totalMinutes} min)`,
                                    children: (
                                        <Flex wrap gap={4}>
                                            {cfg.flow.map((s, i) => (
                                                <Tag key={i} icon={<StepKindIcon kind={s.kind} />} color={s.kind === 'breakout' ? 'blue' : 'default'} style={{ margin: 0 }}>
                                                    {labels[i]} · {s.minutes}′
                                                </Tag>
                                            ))}
                                        </Flex>
                                    ),
                                },
                            ]}
                        />
                        <Alert
                            type="warning"
                            showIcon
                            title={currentGroups.length ? 'This replaces the current setup' : 'This becomes the setup'}
                            description={(
                                <ul className="gathering-import-notes">
                                    <li>Groups, ideal room, topics and flow are replaced by the file's.</li>
                                    {kept.length > 0 && (
                                        <li>People keep their picks for groups with the same name: {kept.map(g => g.name).join(', ')}.</li>
                                    )}
                                    {dropped.length > 0 && (
                                        <li>Groups not in the file are removed, with their picks: {dropped.map(g => g.name).join(', ')}.</li>
                                    )}
                                    <li>Published rooms are cleared — plan and publish again after importing.</li>
                                </ul>
                            )}
                        />
                    </Flex>
                )}
            </Modal>
        </>
    );
}

/**
 * Export / Import of the gathering's setup as a JSON file (see
 * utils/gathering_config.js) — so a setup built on one node can be reused on
 * another.
 *
 *   buildConfig(): the config for what the admin view shows now
 *   importBlocked: a reason import isn't possible right now, or null
 */
function ConfigShare({ buildConfig, currentGroups, importBlocked, paletteColor, onImport }) {
    return (
        <Space size={8}>
            <Tooltip title="Download this gathering's setup — groups, ideal room, topics and flow — to use on another node">
                <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadGatheringConfig(buildConfig())}>
                    Export
                </Button>
            </Tooltip>
            <ConfigImport currentGroups={currentGroups} paletteColor={paletteColor} onImport={onImport}>
                {({ uploadProps }) => (
                    <Tooltip title={importBlocked || 'Replace this setup with one from a file'}>
                        <Upload {...uploadProps} disabled={!!importBlocked}>
                            <Button size="small" icon={<UploadOutlined />} disabled={!!importBlocked}>Import</Button>
                        </Upload>
                    </Tooltip>
                )}
            </ConfigImport>
        </Space>
    );
}

export default ConfigShare;
